import { takeLatest, put, call, all } from 'redux-saga/effects';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import {
  PapiStudioSettingsGallery,
  PapiStudioSettingsGallerySave,
  PapiRequestStudioSettingsGallerySave,
  PapiDaSharedGallectionFolders,
  PapiRequestDaSharedGallectionFolders,
} from '@wix/da-papi-types';
import { ALL_FOLDER_ID } from '@wix/da-url';
import i18next from 'i18next';
import {
  actions as notificationActions,
  NotificationType,
} from '@wix/da-shared-react/pkg/Notifications';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { GalleryOrder, GallerySettings } from '../../types/gallerySettings';
import {
  loadSettings,
  loadSettingsSuccess,
  loadSettingsFailure,
  loadGalleries,
  loadGalleriesSuccess,
  loadGalleriesFailure,
  saveSettings,
  saveSettingsSuccess,
  saveSettingsFailure,
} from '../../actions/studio/gallerySettings';

// Helper to parse response into GallerySettings
const parseSettingsResponse = (response: PapiStudioSettingsGallery): GallerySettings => ({
  folderId: response.folderId ?? ALL_FOLDER_ID,
  order: response.order || GalleryOrder.PERSONALIZED,
});

function* loadSettingsSaga() {
  try {
    const response: PapiStudioSettingsGallery = yield call(
      requestPuppy,
      {
        method: 'get',
        url: 'settings/gallery',
      },
      undefined,
      'studio'
    );

    if (isApiError(response)) {
      const errorMsg = i18next.t('galleryDisplaySettings.loadError');
      yield putErrorNotification(errorMsg);
      yield put(loadSettingsFailure(errorMsg));
      return;
    }

    const settings = parseSettingsResponse(response);
    yield put(loadSettingsSuccess(settings));

    // Automatically load galleries after settings load
    yield put(loadGalleries());
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : i18next.t('galleryDisplaySettings.unknownError');
    yield putErrorNotification(errorMessage);
    yield put(loadSettingsFailure(errorMessage));
  }
}

function* saveSettingsSaga(action: ReturnType<typeof saveSettings>) {
  try {
    const { folderId, order } = action.payload;

    const requestData: PapiRequestStudioSettingsGallerySave = {
      folderid: folderId,
      order,
    };

    const response: PapiStudioSettingsGallerySave = yield call(
      requestPuppy,
      {
        method: 'post',
        url: 'settings/gallery/save',
        data: requestData,
      },
      undefined,
      'studio'
    );

    if (isApiError(response) || response.success !== true) {
      const errorMsg = i18next.t('galleryDisplaySettings.saveError');
      yield putErrorNotification(errorMsg);
      yield put(saveSettingsFailure(errorMsg));
      return;
    }

    const settings: GallerySettings = {
      folderId,
      order,
    };

    yield put(saveSettingsSuccess(settings));
    yield put(
      notificationActions.addNotification(NotificationType.Generic, {
        messageLocaleKey: 'galleryDisplaySettings.saveSuccess',
        cardProps: {
          duration: NotificationDuration.S,
        },
      })
    );
  } catch (err) {
    const errorMsg = i18next.t('galleryDisplaySettings.saveError');
    const errorMessage =
      err instanceof Error ? err.message : i18next.t('galleryDisplaySettings.unknownError');
    yield putErrorNotification(errorMsg);
    yield put(saveSettingsFailure(errorMessage));
  }
}

function* loadGalleriesSaga() {
  try {
    const requestData: PapiRequestDaSharedGallectionFolders = {
      type: 'gallery',
    };

    const response: PapiDaSharedGallectionFolders = yield call(requestPuppy, {
      method: 'get',
      url: 'gallection/folders',
      params: requestData,
    });

    if (isApiError(response)) {
      const errorMsg = i18next.t('galleryDisplaySettings.loadError');
      yield putErrorNotification(errorMsg);
      yield put(loadGalleriesFailure(errorMsg));
      return;
    }

    yield put(loadGalleriesSuccess(response.results || []));
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : i18next.t('galleryDisplaySettings.unknownError');
    yield putErrorNotification(errorMessage);
    yield put(loadGalleriesFailure(errorMessage));
  }
}

export default function* gallerySettingsSaga() {
  yield all([
    takeLatest(loadSettings.toString(), loadSettingsSaga),
    takeLatest(loadGalleries.toString(), loadGalleriesSaga),
    takeLatest(saveSettings.toString(), saveSettingsSaga),
  ]);
}
